import React, { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { observer } from "mobx-react-lite"
import {
  Table,
  Button,
  Loader,
  Tooltip,
  Group,
  Text,
  ActionIcon,
  Title,
  Badge,
  Stack,
  Flex, ScrollArea,
} from "@mantine/core"
import { IconPencil } from "@tabler/icons-react"
import { API_URL } from "shared/config"
import InfiniteScroll from "react-infinite-scroll-component"
import { Link } from "react-router-dom"
import { type Article } from "../../shared/types/article"
import { Image } from "../../shared/ui/Image"
import { categoryNames } from "../../shared/lib/constants"
import { PATHS } from "../../shared/lib/paths"
import {
  ArticlePublishedCheckbox,
  ArticlePublishedModal,
  ArticleDeleteModal,
  ArticleSearch,
} from "../../features/article"
import { ArticleStore } from "../../app/store"
import { ErrorResult } from "../../shared/ui/ErrorResult"
import { EmptyResult } from "../../shared/ui/EmptyResult"

// ----------------------------------------------------------------------

export function ArticleRow(article: Article): React.JSX.Element {
  const { title, category, _id, cover, published } = article

  return (
    <tr>
      <td>
        <Flex gap="xl">
          <Image src={`${API_URL}/media/${cover}`} ratio="1/1" sx={{ maxWidth: 140, borderRadius: 16 }}/>
          <Stack spacing="md">
            <Text component={Link} fz="xl" fw={600} to={PATHS.BLOG.DETAILS(_id)}>
              {title}
            </Text>
            <Badge color="gray" w="fit-content">
              {categoryNames[category]}
            </Badge>
          </Stack>
        </Flex>
      </td>
      <td>
        <Badge color={published ? "green" : "red"} fullWidth>
          {published ? "Да" : "Нет"}
        </Badge>
      </td>
      <td>
        <Group position="right" noWrap>
          <ArticlePublishedModal {...article} />
          <Tooltip label="Редактировать">
            <ActionIcon radius="xl" size="xl" component={Link} to={PATHS.DASHBOARD.BLOG.EDIT(_id)}>
              <IconPencil stroke={1.5}/>
            </ActionIcon>
          </Tooltip>
          <ArticleDeleteModal {...article} />
        </Group>
      </td>
    </tr>
  )
}

export const ArticleTablePage = observer(function ArticleTablePage() {
  const { articles, isLoading, totalCount, loadMore, initialLoad, error } = ArticleStore

  useEffect(() => {
    void initialLoad()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderInfo = () => {
    if (isLoading) {
      return (
        <Flex mt="md" direction="column" align="center" justify="center" sx={{ flex: 1 }}>
          <Loader size="lg" variant="dots"/>
          <Text size="lg" mt="xs">
            Загружаем данные...
          </Text>
        </Flex>
      )
    }

    if (error) {
      return <ErrorResult errorMessage={error}/>
    }

    if (!articles.length) {
      return <EmptyResult/>
    }

    return (
      <Group position="center">
        <Badge color="gray" size="lg" mt="xl">
          Вы загрузили все статьи
        </Badge>
      </Group>
    )
  }

  return (
    <>
      <Helmet>
        <title>Статьи - админка</title>
      </Helmet>

      <Flex direction="column" w="100%">
        <Group position="apart" spacing="xl" mb="xl" pb="md">
          <Title order={1} size={24}>
            Статьи
          </Title>
          <Button component={Link} to={PATHS.DASHBOARD.BLOG.CREATE}>Создать статью</Button>
        </Group>

        <Group position="apart" mb="sm" spacing="xl">
          <ArticleSearch size="md" sx={{ flex: "auto" }}/>
          <ArticlePublishedCheckbox/>
        </Group>

        <ScrollArea sx={{ flex: "auto" }}>
          <InfiniteScroll
            next={loadMore}
            dataLength={articles.length}
            hasMore={totalCount !== articles.length}
            loader={<Flex mt="md" direction="column" align="center" justify="center" sx={{ flex: 1 }}>
              <Loader size="lg" variant="dots"/>
              <Text size="lg" mt="xs">
                Загружаем данные...
              </Text>
            </Flex>}
            endMessage={renderInfo()}
            style={{ overflow: "unset" }}
          >
            <Table highlightOnHover sx={{ minWidth: 800 }} verticalSpacing="sm">
              <thead>
              <tr>
                <th>Название</th>
                <th style={{ textAlign: "center" }}>Опубликована</th>
                <th style={{ textAlign: "right" }}>Действия</th>
              </tr>
              </thead>
              <tbody>
              {articles.map((article) => (
                <ArticleRow key={article._id} {...article} />
              ))}
              </tbody>
            </Table>
          </InfiniteScroll>
        </ScrollArea>

      </Flex>
    </>
  )
})
